import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, Inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ErrorConstants } from "src/app/shared/constants/error.constants";
import { UserService } from "src/app/services/user.service";
import { getEnumList } from "src/app/_helpers/enum-helper";
import { RiskAssessmentService } from "src/app/services/risk-assessment.service";
import { AgentService } from "src/app/services/agent.service";
import { forkJoin, of } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import { Lookup } from "src/app/models/lookup.model";
import { RiskAssessmentSummary } from "src/app/models/risk-assessment-summary";
import { LikelihoodColorLevel } from "src/app/models/risk-assessment.model";
import { parseLikelihoodColorLevels } from "src/app/shared/utils/risk-level-parsing.util";
import { TenantRiskService } from "src/app/services/tenantRisk.service";
import { ToastService } from "src/app/services/toast.service";
import { PdfExportService } from "src/app/services/pdf-export.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import nameOf from "src/app/shared/form/nameOf.helper";
import { TranslateService } from "src/app/services/translate.service";
import { JurisdictionRiskConfigService } from 'src/app/services/Jurisdiction-risk-config.service';
import { downloadBlob } from 'src/app/shared/utils/download.util';
import { parseQualitativeDimensionLabels } from 'src/app/shared/utils/qualitative-config.util';

export enum WorkersContractors {
  workers,
  Contractors
}

export class LookupSelector extends Lookup {
  selected?: boolean
}

interface WorkerContractorOption {
  value: string;
  selected: boolean;
}

interface HealthRiskDisplay {
  numberOfWorkers: number;
  numberOfContractors: number;
  displayValue: string;
}

type RiskAssessmentSummaryDisplay = Omit<RiskAssessmentSummary, 'healthRiskScores'> & {
  healthRiskScores: HealthRiskDisplay[];
};

@Component({
  standalone: false,
  selector: 'app-risk-assessment-report-popup',
  templateUrl: './risk-assessment-report-popup.component.html',
  styleUrl: './risk-assessment-report-popup.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None
})
export class RiskAssessmentReportPopupComponent implements OnInit {
  assessmentIds: string[] = [];
  agentsList: LookupSelector[] = [];
  workersContractorsList: WorkerContractorOption[] = [];
  agentAllSelected = false;
  wcAllSelected = false;
  dataSource: RiskAssessmentSummaryDisplay[] = [];
  isTableLoading = false;
  displayedColumns = ['agentName'];
  noDataError = ErrorConstants.errorHtml;
  currentDate = new Date();
  logoUrl = '';
  summaryList: RiskAssessmentSummary[] = [];
  exposureLikelihoodLevels: LikelihoodColorLevel[] = [];
  likelihoodColumns: { id: string; level: LikelihoodColorLevel }[] = [];
  columnHeaders: string[] = [];
  columnLabelMode: 'likelihood' | 'exposureBand' = 'likelihood';
  private tenantLikelihoodLevels: LikelihoodColorLevel[] = [];
  qualitativeExceedanceLabel: string;
  qualitativeFrequencyLabel: string;
  isQualitativeConfigLoaded = false;

  constructor(
    public dialogRef: MatDialogRef<RiskAssessmentReportPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { assessmentIds: string[] },
    private userService: UserService,
    private riskAssessmentService: RiskAssessmentService,
    private agentService: AgentService,
    private tenantRiskService: TenantRiskService,
    private toastService: ToastService,
    private pdfService: PdfExportService,
    private dateFormatService: DateFormatService,
    private translateService: TranslateService,
    private jurisdictionRiskConfigService: JurisdictionRiskConfigService
  ) {
    this.qualitativeExceedanceLabel = this.translateService.getDynamicTranslation('Exceedance Probability');
    this.qualitativeFrequencyLabel = this.translateService.getDynamicTranslation('Frequency of Exposure');
    this.workersContractorsList = getEnumList(WorkersContractors).map(val => ({
      value: val,
      selected: false
    }));
    this.assessmentIds = data.assessmentIds;
  }

  ngOnInit(): void {
    const firstAssessmentId = this.assessmentIds?.[0];

    forkJoin({
      agent: this.agentService.getAll(),
      logoUrl: this.userService.getTenantLogoUrl(),
      tenantRisk: this.tenantRiskService.getCurrent(),
      assessment: firstAssessmentId
        ? this.riskAssessmentService.getById(firstAssessmentId).pipe(catchError(() => of(null)))
        : of(null),
    }).pipe(
      tap(result => {
        this.logoUrl = result.logoUrl ?? "";
        this.agentsList = result.agent.map((agent) => ({
          ...agent,
          selected: false,
        }));
        // Baseline from tenant; the jurisdiction config (fetched next) overrides when defined.
        this.tenantLikelihoodLevels = parseLikelihoodColorLevels(result.tenantRisk?.exposureLikelihoodLevels);
        this.exposureLikelihoodLevels = this.tenantLikelihoodLevels;
      }),
      // The summary fetch depends only on agentsList/assessmentIds (set above), not on the
      // jurisdiction config, so run both in parallel to avoid an extra sequential round-trip.
      switchMap(result => {
        const jurisdictionId = result.assessment?.jurisdictionId;
        const configCall = jurisdictionId
          ? this.jurisdictionRiskConfigService.getByJurisdictionId(jurisdictionId).pipe(catchError(() => of(null)))
          : of(null);
        const summaryCall = this.riskAssessmentService.getRiskAssessmentSummary({
          riskAssessmentIds: this.assessmentIds,
          agentIds: this.agentsList.map(value => value.id),
        });
        return forkJoin({ config: configCall, summary: summaryCall });
      }),
      // Prefer the Jurisdiction Risk Configuration over tenant values so the report matches the
      // Risk Register cards for any jurisdiction that overrides risk levels.
      tap(({ config }) => {
        this.applyQualitativeDimensionLabels(config?.qualitativeConfig);
        const jurisdictionLikelihoodLevels = parseLikelihoodColorLevels(config?.exposureLikelihoodLevels);
        this.exposureLikelihoodLevels = jurisdictionLikelihoodLevels.length > 0
          ? jurisdictionLikelihoodLevels
          : this.tenantLikelihoodLevels;
      })
    ).subscribe({
      next: ({ summary }) => {
        this.summaryList = summary;
        this.buildColumns();
      },
      error: () => {
        this.toastService.displayErrorMessage('Error getting Risk Assessment Summary', true);
      }
    });
  }

  get likelihoodDimensionsLabel(): string {
    return `${this.qualitativeExceedanceLabel} × ${this.qualitativeFrequencyLabel}`;
  }

  // Builds one table column per configured likelihood level. The API already groups the
  // worker/contractor counts by likelihood score, so the report must show likelihood ratings —
  // not health-risk ratings (consequence × likelihood), which was the previous incorrect display.
  private buildColumns(): void {
    this.likelihoodColumns = this.exposureLikelihoodLevels.map(level => ({
      id: `level-${level.key}`,
      level,
    }));
    this.displayedColumns = [
      nameOf<RiskAssessmentSummary>(x => x.agentName),
      ...this.likelihoodColumns.map(column => column.id),
    ];
    this.refreshColumnHeaders();
    this.updateTableData();
  }

  private refreshColumnHeaders(): void {
    this.columnHeaders = this.likelihoodColumns.map(column => this.labelForLevel(column.level));
  }

  // The Likelihood mode shows the level Name; the Exposure Band mode shows its QuantitativeName
  // (the equivalent quantitative risk category), for any assessment method. Falls back to N/A
  // when the configured label is missing.
  private labelForLevel(level?: LikelihoodColorLevel): string {
    const label = this.columnLabelMode === 'exposureBand' ? level?.QuantitativeName : level?.Name;
    return label?.trim() || this.translateService.getDynamicTranslation('N/A');
  }

  private labelForScore(score: number): string {
    return this.labelForLevel(this.exposureLikelihoodLevels.find(level => level.key === score));
  }

  onColumnLabelModeChange(): void {
    this.refreshColumnHeaders();
  }

  private applyQualitativeDimensionLabels(rawQualitativeConfig?: string): void {
    const labels = parseQualitativeDimensionLabels(rawQualitativeConfig, {
      exceedanceLabel: this.qualitativeExceedanceLabel,
      frequencyLabel: this.qualitativeFrequencyLabel,
    });
    this.qualitativeExceedanceLabel = labels.exceedanceLabel;
    this.qualitativeFrequencyLabel = labels.frequencyLabel;
    this.isQualitativeConfigLoaded = true;
  }

  selectionChange(list: Array<{ selected?: boolean }>, select: boolean, allKey: 'agentAllSelected' | 'wcAllSelected' | '' = '') {
    if (!allKey) {
      list.forEach(item => item.selected = select);
    } else {
      this[allKey] = list.every(item => item.selected === true);
    }
    this.updateTableData();
  }

  updateTableData() {
    this.isTableLoading = true;
    const selectedAgents = this.agentsList.filter(agent => agent.selected).map(res => res.id)
    const filteredSummary = this.summaryList.filter(summary => selectedAgents.includes(summary.agentId))
      .map(summary => ({ ...summary }));
    const selectedOptions = this.workersContractorsList.filter(wc => wc.selected).map(wc => wc.value);
    this.dataSource = filteredSummary.map((summary) => {
      const healthRiskScores = this.likelihoodColumns.map(column => {
        const match = summary.healthRiskScores.find(score => score.likelihoodScore === column.level.key);
        const numberOfWorkers = match?.numberOfWorkers ?? 0;
        const numberOfContractors = match?.numberOfContractors ?? 0;

        let displayValue = '';
        if (selectedOptions.includes(WorkersContractors[0])) {
          displayValue += `${this.translateService.getDynamicTranslation('W')}: ${numberOfWorkers} `;
        }
        if (selectedOptions.includes(WorkersContractors[1])) {
          displayValue += `${this.translateService.getDynamicTranslation('C')}: ${numberOfContractors} `;
        }
        return {
          numberOfWorkers,
          numberOfContractors,
          displayValue: displayValue.trim() || 'N/A',
        } as HealthRiskDisplay;
      });

      return {
        ...summary,
        healthRiskScores,
      } as RiskAssessmentSummaryDisplay;
    });
    this.isTableLoading = false;
  }

  cancel() {
    this.dialogRef.close();
  }

  save(orientation: "portrait" | "landscape" = 'landscape') {
    const dateString = (date: number | Date) =>
      this.dateFormatService.formatTimestampToString(
        typeof date === 'number' ? date : date.getTime(),
        this.dateFormatService.userDateFormat,
      );
    setTimeout(async () => {
      const doc = await this.pdfService.createDocument(orientation);

      if (this.logoUrl) {
        this.pdfService.addContent(doc, this.logoUrl, {
          type: 'image', width: 60, height: 30,
          align: 'left', spacing: 20, sameLine: true
        });
      }

      this.pdfService.addContent(doc, this.translateService.getDynamicTranslation('Report Date: {{date}}', { date: dateString(this.currentDate) }), {
        align: 'right', fontSize: 10, spacing: this.logoUrl ? 0 : 20,
        sameLine: this.logoUrl ? true : false
      });

      this.pdfService.addTitle(doc, this.translateService.getDynamicTranslation('Risk Assessment Report'));

      const headerRow = [
        this.translateService.getDynamicTranslation('Agent Name'),
        ...this.columnHeaders,
      ];

      const bodyRows = this.dataSource.map(row => {
        const rowData = [];
        rowData.push(row.agentName);

        row.healthRiskScores.forEach((score, index) => {
          const level = this.likelihoodColumns[index]?.level;
          if (level) {
            rowData.push({
              content: score.displayValue,
              styles: {
                fillColor: this.pdfService.toRgbArray(level.Color)
              }
            });
          } else {
            rowData.push(score.displayValue);
          }
        });

        return rowData;
      });

      await this.pdfService.addTable(doc, headerRow, bodyRows);

      this.pdfService.addContent(doc, this.translateService.getDynamicTranslation('Included Risk Assessment IDs: {{ids}}', { ids: this.assessmentIds.join(', ') }), {
        bold: true, spacing: 20
      });

      this.pdfService.saveDocument(doc, 'risk-assessment-report.pdf');
    }, 100);
  }

  async exportToExcel() {
    const [XLSX] = await Promise.all([
      import(/* webpackChunkName: "xlsx" */ 'xlsx')
    ]);
    const wb = XLSX.utils.book_new();
    const selectedAgents = this.agentsList.filter(agent => agent.selected).map(res => res.id);
    const filteredSummary = this.summaryList.filter(summary => selectedAgents.includes(summary.agentId))
      .map(summary => ({ ...summary }));
    filteredSummary.forEach(agent => {
      const wsData = [
        [{ v: agent.agentName, t: 's', s: { font: { bold: true, sz: 14 } } }],
        [{ v: '', t: 's' }],
        [{ v: this.translateService.getDynamicTranslation('Total Number of Employees exposed to Agent'), t: 's' }, { v: agent.totalNumberOfWorkers, t: 'n' }],
        [{ v: this.translateService.getDynamicTranslation('Total Number of Contractors exposed to Agent'), t: 's' }, { v: agent.totalNumberOfContractors, t: 'n' }],
        [{ v: '', t: 's' }]
      ];

      agent.healthRiskScores.forEach(score => {
        wsData.push([
          {
            v: this.translateService.getDynamicTranslation('Number of Employees with Exposure Likelihood Score {{score}}', { score: this.labelForScore(score.likelihoodScore) }),
            t: 's'
          },
          { v: score.numberOfWorkers, t: 'n' }
        ]);
      });

      wsData.push([{ v: '', t: 's' }]);

      agent.healthRiskScores.forEach(score => {
        wsData.push([
          {
            v: this.translateService.getDynamicTranslation('Number of Contractors with Exposure Likelihood Score {{score}}', { score: this.labelForScore(score.likelihoodScore) }),
            t: 's'
          },
          { v: score.numberOfContractors, t: 'n' }
        ]);
      });

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [{ wch: 80 }, { wch: 20 }];
      const cleanName = agent.agentName.replace(/[:\\/?*\[\]]/g, '').substring(0, 30);
      XLSX.utils.book_append_sheet(wb, ws, cleanName);
    });

    if (wb.SheetNames.length === 0) {
      this.toastService.showInfo('Workbook is empty. No sheets were added', true)
      return;
    }

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
    const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
    downloadBlob(data, 'exported_Risk_Assessment.xlsx');
  }

  public trackById = trackById;
  public trackByIndex = trackByIndex;
}
